import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import Button from '../../ui/Button'
import PageLoader from '../../ui/PageLoader'
import PageHeader from '../../layout/PageHeader'
import { useProjects } from '../../../contexts/ProjectsContext'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { deleteTeamMember, fetchTeamMembers } from '../../../api/teamMembersApi'
import type { TeamMember } from '../../../types/teamMember'
import TeamMemberFilters from './TeamMemberFilters'
import TeamMembersTable from './TeamMembersTable'
import TeamMemberFormModal from './TeamMemberFormModal'

export default function TeamMembersPage() {
  const { id: projectId = '' } = useParams()
  const [searchParams] = useSearchParams()
  const { getProject, loading: projectsLoading } = useProjects()
  const project = projectId ? getProject(projectId) : undefined

  // When the user arrives from the Floor Sheet (or any page passing a
  // `returnTo`), the back button should return them there instead of jumping
  // all the way up to the project hub.
  const returnToParam = searchParams.get('returnTo')?.trim() || ''

  const [items, setItems] = useState<TeamMember[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 10
  const [listLoading, setListLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')
  const debouncedSearch = useDebouncedValue(search, 350)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TeamMember | null>(null)

  const loadList = useCallback(async () => {
    if (!projectId) return
    setListLoading(true)
    try {
      const res = await fetchTeamMembers(projectId, {
        search: debouncedSearch.trim() || undefined,
        role: role.trim() || undefined,
        status: status === 'active' || status === 'inactive' ? status : undefined,
        page,
        pageSize
      })
      setItems(res.items)
      setTotal(res.total)
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load team members')
    } finally {
      setListLoading(false)
    }
  }, [projectId, debouncedSearch, role, status, page, pageSize])

  useEffect(() => {
    void loadList()
  }, [loadList])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, role, status])

  const handleDelete = async (m: TeamMember) => {
    if (!window.confirm(`Remove ${m.name} from this project?`)) return
    const prev = items
    setItems((xs) => xs.filter((x) => x.id !== m.id))
    setTotal((t) => Math.max(0, t - 1))
    try {
      await deleteTeamMember(projectId, m.id)
      toast.success('Team member removed')
    } catch (e: unknown) {
      setItems(prev)
      setTotal((t) => t + 1)
      toast.error(e instanceof Error ? e.message : 'Delete failed')
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  if (projectsLoading) {
    return <PageLoader label="Loading project…" />
  }

  if (!project) {
    return (
      <div className="card p-6">
        <p className="text-sm text-gray-600">Project not found.</p>
        <Link to="/resources" className="mt-4 inline-block text-sm font-medium text-primary-600 hover:underline">
          Back to Project Hub
        </Link>
      </div>
    )
  }

  if (listLoading && items.length === 0) {
    return <PageLoader label="Loading team members…" />
  }

  return (
    <div className="flex min-h-[calc(100vh-6rem)] flex-col pb-24">
      <div className="flex flex-1 flex-col space-y-6">
        <PageHeader
          backTo={
            returnToParam
              ? { to: returnToParam, label: 'Back to floor sheet' }
              : { to: `/projects/${project.id}/next`, label: 'Back to project' }
          }
          title={
            <>
              <span>Team Members</span>
              <span className="text-heritage-grey/60">•</span>
              <span className="truncate text-text-grey">{project.name}</span>
            </>
          }
          subtitle={<span>Manage who can access this project.</span>}
          actions={
            <>
              <Button
                type="button"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                Add Team Member
              </Button>
            </>
          }
        />

        <TeamMemberFilters
          search={search}
          onSearchChange={setSearch}
          role={role}
          onRoleChange={setRole}
          status={status}
          onStatusChange={setStatus}
        />

        <div className="relative flex-1">
          {listLoading && items.length > 0 ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
              <PageLoader label="Loading team members…" compact />
            </div>
          ) : null}
          <TeamMembersTable
            items={items}
            listLoading={listLoading}
            onEdit={(m) => {
              setEditing(m)
              setFormOpen(true)
            }}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white px-6 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] lg:left-64">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 text-sm text-gray-600 sm:flex-row">
          <span>
            Page {page} of {totalPages} · {total} total
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <TeamMemberFormModal
        projectId={project.id}
        project={project}
        open={formOpen}
        member={editing}
        existingMemberIds={items.map((m) => m.memberExternalId)}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        onSaved={() => void loadList()}
      />
    </div>
  )
}
